import { useEffect, useState, useCallback, useRef, type ReactNode } from 'react';
import { toast } from 'react-hot-toast';
import { authService } from '@/features/user/services/authService';
import type { PublicUserResponse, SignupFormData } from '@/features/user/types/types';
import { AUTH_EVENTS } from './authEvents';
import { AuthContext, type AuthContextType } from './AuthContext';

interface AuthProviderProps {
  children: ReactNode;
}

const STORAGE_KEY = 'auth-storage';
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

interface PersistedAuthState {
  user: PublicUserResponse | null;
  isAuthenticated: boolean;
  pendingSignupData: SignupFormData | null;
  isEmailVerified: boolean;
  lastValidated: number; // timestamp of last successful validation
}

export default function AuthProvider({ children }: AuthProviderProps) {
  // Load persisted state from localStorage on mount
  const loadPersistedState = (): PersistedAuthState | null => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored) as PersistedAuthState;
      }
    } catch (error) {
      console.error('Error loading persisted auth state:', error);
    }
    return null;
  };

  // Save state to localStorage
  const savePersistedState = useCallback((state: PersistedAuthState) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error('Error saving persisted auth state:', error);
    }
  }, []);

  // Check if cached data is still valid
  const isCacheValid = useCallback((lastValidated: number | undefined): boolean => {
    if (!lastValidated) return false;
    const now = Date.now();
    return (now - lastValidated) < CACHE_DURATION_MS;
  }, []);

  // Initialize from persisted state for fast initial render
  const persistedState = loadPersistedState();
  const cacheIsValid = isCacheValid(persistedState?.lastValidated);
  
  const [user, setUser] = useState<PublicUserResponse | null>(persistedState?.user ?? null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(persistedState?.isAuthenticated ?? false);
  const [isLoading, setIsLoading] = useState<boolean>(!cacheIsValid); // Only show loading if we need to validate
  const [error, setError] = useState<string | null>(null);
  const [pendingSignupData, setPendingSignupData] = useState<SignupFormData | null>(persistedState?.pendingSignupData ?? null);
  const [isEmailVerified, setIsEmailVerified] = useState<boolean>(persistedState?.isEmailVerified ?? false);
  const [lastValidated, setLastValidated] = useState<number>(persistedState?.lastValidated ?? Date.now());

  // Track if we're currently validating to prevent loops
  const isValidatingRef = useRef(false);

  // Persist state whenever relevant fields change
  useEffect(() => {
    savePersistedState({
      user,
      isAuthenticated,
      pendingSignupData,
      isEmailVerified,
      lastValidated,
    });
  }, [user, isAuthenticated, pendingSignupData, isEmailVerified, lastValidated, savePersistedState]);

  const validateAuth = useCallback(async (force = false) => {
    // Prevent multiple simultaneous validation calls (unless forced)
    if (!force && isValidatingRef.current) return;
    isValidatingRef.current = true;
    
    setIsLoading(true);
    try {
      const userData = await authService.validate();
      if (userData) {
        setUser(userData);
        setIsAuthenticated(true);
        // Update lastValidated timestamp on successful validation
        const now = Date.now();
        setLastValidated(now);
        setError(null); // Clear any previous errors on success
      } else {
        // 401 error - user is not authenticated
        setUser(null);
        setIsAuthenticated(false);
        // Clear persisted state if validation fails
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      // Check if it's a non-401 error
      const isNon401Error = error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { status?: number } }).response?.status !== 401
        : true; // If we can't determine status, assume it's a non-401 error (network, etc.)
      
      if (isNon401Error) {
        // Non-401 error - keep user logged in, show toast
        console.error('Error validating authentication (non-401):', error);
        toast.error('Service temporarily unavailable. Please try again.');
        setError('Service temporarily unavailable. Please try again.');
        // Don't clear user state or localStorage - keep the session
      } else {
        // 401 error - user is not authenticated
        console.error('Error validating authentication (401):', error);
        setUser(null);
        setIsAuthenticated(false);
        // Clear persisted state on error
        localStorage.removeItem(STORAGE_KEY);
      }
    } finally {
      setIsLoading(false);
      isValidatingRef.current = false;
    }
  }, []);

  useEffect(() => {
    // Only validate on mount if cache is invalid or doesn't exist
    if (!cacheIsValid) {
      validateAuth();
    }
    // If cache is valid, we can skip the API call and use persisted data
  }, [cacheIsValid, validateAuth]);

  // Listen for logout events to immediately update state
  useEffect(() => {
    const handleLogout = () => {
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
      setError(null);
      setPendingSignupData(null);
      setIsEmailVerified(false);
      localStorage.removeItem(STORAGE_KEY);
    };

    const handleRefetch = () => {
      if (isValidatingRef.current) return;
      isValidatingRef.current = true;
      validateAuth(true).finally(() => {
        isValidatingRef.current = false;
      });
    };

    window.addEventListener(AUTH_EVENTS.LOGOUT, handleLogout);
    window.addEventListener(AUTH_EVENTS.REFETCH, handleRefetch);

    return () => {
      window.removeEventListener(AUTH_EVENTS.LOGOUT, handleLogout);
      window.removeEventListener(AUTH_EVENTS.REFETCH, handleRefetch);
    };
  }, [validateAuth]);

  // Actions
  const handleSetUser = useCallback((newUser: PublicUserResponse | null) => {
    setUser(newUser);
    setIsAuthenticated(!!newUser);
    if (newUser) {
      setLastValidated(Date.now());
    }
  }, []);

  const handleLogin = useCallback((newUser: PublicUserResponse) => {
    setUser(newUser);
    setIsAuthenticated(true);
    setError(null);
    setLastValidated(Date.now());
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Error during logout API call:', error);
    }
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
    setPendingSignupData(null);
    setIsEmailVerified(false);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isGuest: user?.is_guest ?? false,
    isLoading,
    error,
    pendingSignupData,
    isEmailVerified,
    setUser: handleSetUser,
    setError,
    clearError: () => setError(null),
    setPendingSignupData,
    setIsEmailVerified,
    login: handleLogin,
    logout: handleLogout,
    refetch: () => validateAuth(true), // Force validation on refetch
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

